/**
 * Pure helpers for the vial label section. No React, no fetch, so every rule here has a
 * one line test.
 */
import type { Tone } from '@/components';
import type { VialAgent, VialJob, VialJobStatus } from './types';

export const VIAL_QTY_DEFAULT = 10;
export const VIAL_QTY_MIN = 1;
export const VIAL_QTY_MAX = 200;

/** Past this the pill turns red: the agent polls every 5 seconds, so 90 is a dead agent. */
export const AGENT_WARN_AFTER_MS = 90_000;

export function clampQty(value: number): number {
  if (!Number.isFinite(value)) return VIAL_QTY_DEFAULT;
  return Math.min(VIAL_QTY_MAX, Math.max(VIAL_QTY_MIN, Math.round(value)));
}

/**
 * The QR on the label, which is the same target the agent renders: dashes dropped, then
 * upper cased, exactly as the agent does (`.replace("-", "").upper()`).
 */
export function qrTarget(batch: string): string {
  return `https://elutide.com/v/${batch.replace(/-/g, '').toUpperCase()}`;
}

/** `seen_at` arrives as unix seconds from WordPress; accept ms or ISO too, to be safe. */
export function agentSeenMs(seenAt: VialAgent['seen_at']): number | null {
  if (seenAt === null || seenAt === undefined) return null;
  if (typeof seenAt === 'number') {
    if (!Number.isFinite(seenAt) || seenAt <= 0) return null;
    return seenAt < 1e12 ? seenAt * 1000 : seenAt;
  }
  const parsed = Date.parse(seenAt);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Job timestamps come from MariaDB as "2026-09-19 14:03:11" in UTC, with no zone marker.
 * Left alone, a browser reads that as local time and every job looks hours old. Anything
 * already ISO shaped passes through untouched.
 */
export function toIsoInstant(value: string | null): string | null {
  if (!value) return null;
  const plain = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(value);
  return plain ? `${plain[1]}T${plain[2]}Z` : value;
}

/** "12 s", "4 min", "2 h", "3 d". Plain, and never more precise than it needs to be. */
export function formatAge(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

export interface AgentStatus {
  tone: Tone;
  text: string;
}

export function agentStatus(seenMs: number | null, nowMs: number): AgentStatus {
  if (seenMs === null) {
    return { tone: 'crit', text: 'Label printer agent: not seen yet' };
  }
  const age = Math.max(0, nowMs - seenMs);
  if (age > AGENT_WARN_AFTER_MS) {
    return { tone: 'crit', text: `Label printer agent: not seen for ${formatAge(age)}` };
  }
  return { tone: 'ok', text: `Label printer agent: seen ${formatAge(age)} ago` };
}

export function isActiveStatus(status: VialJobStatus): boolean {
  return status === 'queued' || status === 'printing';
}

export function hasActiveJob(jobs: readonly VialJob[] | undefined): boolean {
  return (jobs ?? []).some((job) => isActiveStatus(job.status));
}

export const STATUS_LABEL: Readonly<Record<VialJobStatus, string>> = {
  queued: 'Queued',
  printing: 'Printing',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const STATUS_TONE: Readonly<Record<VialJobStatus, Tone>> = {
  queued: 'info',
  printing: 'copper',
  done: 'ok',
  failed: 'crit',
  cancelled: 'neutral',
};
